import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegionState } from '../contexts/RegionContext';
import { isCustomDomain } from '../utils/routing';
import {
  CareerSiteNotFoundError,
  detectCareerRoute,
  fetchCareerPage,
  HOST_PROBE_TIMEOUT_MS,
  isCareerPageResponse,
  normalisePageResponse,
  rememberHostWithoutCareerSite,
} from './resolver';
import type { CareerPageResponse, CareerRoute } from './types';

const CareerSiteApp = lazy(() => import('./CareerSiteApp'));

type HostDecision = 'career' | 'legacy';

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
    </div>
  );
}

interface CareerGateProps {
  /** Today's routes, rendered untouched whenever the URL is not a career site. */
  children: ReactNode;
}

/**
 * The one branch between career-site mode and today's routes.
 *
 * Path mode (`/career/<slug>/…`) and the preview are always career mode. On a
 * custom domain the platform is asked once per host whether it serves a career
 * site; a 404 means "no", is remembered, and today's routes take over.
 */
export default function CareerGate({ children }: CareerGateProps) {
  const location = useLocation();
  const route = detectCareerRoute(location.pathname, window.location.hostname, isCustomDomain());

  if (!route) return <>{children}</>;

  if (route.mode === 'host') {
    return <HostGate route={route} legacy={children} />;
  }

  return (
    <Suspense fallback={<Spinner />}>
      <CareerSiteApp route={route} />
    </Suspense>
  );
}

function HostGate({ route, legacy }: { route: Extract<CareerRoute, { mode: 'host' }>; legacy: ReactNode }) {
  const regionState = useRegionState();
  // Multi-region builds already probed `/career/page` to find the region; the
  // answer doubles as the first page so it is not fetched twice.
  const probed = useMemo(
    () => (regionState?.identifierType === 'career' && isCareerPageResponse(regionState.probeData)
      ? normalisePageResponse(regionState.probeData)
      : null),
    [regionState],
  );
  const [decision, setDecision] = useState<HostDecision | null>(() => {
    if (probed) return 'career';
    if (regionState && regionState.identifierType !== 'career') return 'legacy';
    return null;
  });
  const [initialData, setInitialData] = useState<CareerPageResponse | null>(probed);

  useEffect(() => {
    if (decision) return;
    let cancelled = false;
    fetchCareerPage(route, route.sitePath, { timeout: HOST_PROBE_TIMEOUT_MS })
      .then((data) => {
        if (cancelled) return;
        setInitialData(data);
        setDecision('career');
      })
      .catch((error) => {
        if (cancelled) return;
        if (error instanceof CareerSiteNotFoundError && error.reason !== 'page') {
          // No site on this host: remembered in localStorage so the next load,
          // and the next mount of this gate, go straight to today's routes. A
          // site that merely has no page under this path is not remembered.
          rememberHostWithoutCareerSite(route.host);
        }
        // Any other failure also falls through to today's routes, which report
        // their own errors; the career lookup never blocks a working site.
        setDecision('legacy');
      });
    return () => { cancelled = true; };
  }, [decision, route]);

  if (decision === 'legacy') return <>{legacy}</>;
  if (decision === null) return <Spinner />;

  return (
    <Suspense fallback={<Spinner />}>
      <CareerSiteApp route={route} initialData={initialData} />
    </Suspense>
  );
}
